import React, { useEffect, useRef, useState } from "react";
import { getUtil } from "@/constants/utilities";
import { AS_HINT_LIST, AS_LABEL_BY_CODE } from "@/constants/asSymbols";

/**
 * SketchCanvas — onsite drawing tool.
 * Supported shape `type`s (all stored with normalized 0..1 coords):
 *   - pen     : freehand polyline (many points)
 *   - line    : straight line (2 points: start, end)
 *   - curve   : arc/curved line — quadratic bezier between 2 points, control
 *               point offset perpendicular to the chord for a natural sweep
 *   - rect    : rectangle bounding box (2 points: opposite corners)
 *   - boxcross: rectangle with an X inside — pit / valve / meter symbol
 *   - ellipse : ellipse in bounding box (2 points)
 *   - arrow   : straight line with arrowhead at end (2 points)
 *   - text    : text label at a single point (1 point + text)
 *   - depth   : text label prefixed "@ Xmm" (1 point + text)
 *   - symbol  : Australian Standard site symbol — bordered circle stamped with
 *               a 2–4 letter code (WM, IO, HYD, GT etc.) at a single point.
 */
const SketchCanvas = ({ job, activeService, brushSize, tool, onChange, backgroundUrl }) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const [current, setCurrent] = useState(null); // active shape being drawn
  const [size, setSize] = useState({ w: 800, h: 600 });
  // Zoom + pan state — CSS transform on the inner layer. Because coord math
  // uses getBoundingClientRect() (which reflects the transformed size) the
  // normalized 0..1 storage doesn't need to change.
  const [zoom, setZoom] = useState({ scale: 1, tx: 0, ty: 0 });
  // Track a two-finger pinch: initial distance, midpoint & starting zoom.
  const pinchRef = useRef(null);
  // Track a middle-click / space-drag pan.
  const panRef = useRef(null);

  // Fit canvas to container
  useEffect(() => {
    const onResize = () => {
      const el = wrapRef.current;
      if (!el) return;
      setSize({ w: el.clientWidth, h: el.clientHeight });
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const drawShape = (ctx, s) => {
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    ctx.lineWidth = s.width;
    const pts = s.points.map(([nx, ny]) => [nx * size.w, ny * size.h]);
    const t = s.type || "pen";

    if (t === "pen") {
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.stroke();
    } else if (t === "line" && pts.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      ctx.lineTo(pts[1][0], pts[1][1]);
      ctx.stroke();
    } else if (t === "curve" && pts.length >= 2) {
      const [x1, y1] = pts[0], [x2, y2] = pts[1];
      // Control point offset perpendicular to the chord (25% of chord length)
      // gives a smooth arc that reads as a natural curved run.
      const midX = (x1 + x2) / 2, midY = (y1 + y2) / 2;
      const dx = x2 - x1, dy = y2 - y1;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len, ny = dx / len; // perpendicular unit vector
      const bow = len * 0.25;
      const cx = midX + nx * bow, cy = midY + ny * bow;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.quadraticCurveTo(cx, cy, x2, y2);
      ctx.stroke();
    } else if (t === "arrow" && pts.length >= 2) {
      const [x1, y1] = pts[0], [x2, y2] = pts[1];
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const head = Math.max(10, s.width * 3);
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6));
      ctx.closePath();
      ctx.fill();
    } else if (t === "rect" && pts.length >= 2) {
      const [x1, y1] = pts[0], [x2, y2] = pts[1];
      ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    } else if (t === "boxcross" && pts.length >= 2) {
      // Rectangle with an X across it — standard AU survey symbol for a pit,
      // valve, meter or utility box.
      const [x1, y1] = pts[0], [x2, y2] = pts[1];
      const left = Math.min(x1, x2), top = Math.min(y1, y2);
      const w = Math.abs(x2 - x1), h = Math.abs(y2 - y1);
      ctx.strokeRect(left, top, w, h);
      ctx.beginPath();
      ctx.moveTo(left, top);
      ctx.lineTo(left + w, top + h);
      ctx.moveTo(left + w, top);
      ctx.lineTo(left, top + h);
      ctx.stroke();
      // Optional pit-type label rendered as a bordered pill above the box.
      if (s.text) {
        const fontSize = Math.max(12, s.width * 2.2);
        ctx.font = `700 ${fontSize}px "Barlow Condensed", sans-serif`;
        ctx.textBaseline = "top";
        const padX = 6, padY = 3;
        const metrics = ctx.measureText(s.text);
        const boxW = metrics.width + padX * 2;
        const boxH = fontSize + padY * 2;
        const bx = left + w / 2 - boxW / 2;
        const by = top - boxH - 2;
        ctx.fillStyle = "#FAFAFA";
        ctx.strokeStyle = s.color;
        ctx.lineWidth = 2;
        ctx.fillRect(bx, by, boxW, boxH);
        ctx.strokeRect(bx, by, boxW, boxH);
        ctx.fillStyle = s.color;
        ctx.fillText(s.text, bx + padX, by + padY);
      }
    } else if (t === "ellipse" && pts.length >= 2) {
      const [x1, y1] = pts[0], [x2, y2] = pts[1];
      const cx = (x1 + x2) / 2, cy = (y1 + y2) / 2;
      const rx = Math.abs(x2 - x1) / 2, ry = Math.abs(y2 - y1) / 2;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if ((t === "text" || t === "depth") && pts.length >= 1 && s.text) {
      const [x, y] = pts[0];
      const fontSize = Math.max(14, s.width * 3);
      ctx.font = `700 ${fontSize}px "Barlow Condensed", sans-serif`;
      ctx.textBaseline = "top";
      // Pill background for readability
      const paddingX = 8, paddingY = 4;
      const metrics = ctx.measureText(s.text);
      const boxW = metrics.width + paddingX * 2;
      const boxH = fontSize + paddingY * 2;
      ctx.fillStyle = "#FAFAFA";
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 2;
      ctx.fillRect(x, y, boxW, boxH);
      ctx.strokeRect(x, y, boxW, boxH);
      ctx.fillStyle = s.color;
      ctx.fillText(s.text, x + paddingX, y + paddingY);
    } else if (t === "symbol" && pts.length >= 1 && s.text) {
      // AS site symbol — bordered white circle stamped with a short code.
      const [x, y] = pts[0];
      const fontSize = Math.max(12, s.width * 2.4);
      ctx.font = `900 ${fontSize}px "Barlow Condensed", sans-serif`;
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      const metrics = ctx.measureText(s.text);
      const radius = Math.max(18, Math.max(metrics.width, fontSize) / 2 + 8);
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = "#FAFAFA";
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = s.color;
      ctx.stroke();
      ctx.fillStyle = s.color;
      ctx.fillText(s.text, x, y);
      // Reset text align so subsequent draws use default (left/top).
      ctx.textAlign = "start";
    }
  };

  // Redraw all shapes + current preview
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = size.w;
    c.height = size.h;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, size.w, size.h);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    (job.strokes || []).forEach((s) => drawShape(ctx, s));
    if (current) drawShape(ctx, current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.strokes, current, size]);

  const posFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return [(clientX - rect.left) / rect.width, (clientY - rect.top) / rect.height];
  };

  // Clamp scale to sensible bounds for a hand-held device.
  const clampScale = (s) => Math.max(0.5, Math.min(6, s));

  // Zoom the canvas keeping the given screen-space focal point stationary.
  // focalX / focalY are in the wrapper's local coordinates (px from top-left).
  const zoomAt = (nextScaleRaw, focalX, focalY) => {
    setZoom((prev) => {
      const nextScale = clampScale(nextScaleRaw);
      if (nextScale === prev.scale) return prev;
      // World point at focal before zoom = (focalX - tx) / scale
      const worldX = (focalX - prev.tx) / prev.scale;
      const worldY = (focalY - prev.ty) / prev.scale;
      const tx = focalX - worldX * nextScale;
      const ty = focalY - worldY * nextScale;
      return { scale: nextScale, tx, ty };
    });
  };

  const zoomBy = (factor) => {
    const wrap = wrapRef.current;
    const cx = wrap ? wrap.clientWidth / 2 : 0;
    const cy = wrap ? wrap.clientHeight / 2 : 0;
    setZoom((prev) => {
      const nextScale = clampScale(prev.scale * factor);
      const worldX = (cx - prev.tx) / prev.scale;
      const worldY = (cy - prev.ty) / prev.scale;
      return { scale: nextScale, tx: cx - worldX * nextScale, ty: cy - worldY * nextScale };
    });
  };

  const resetZoom = () => setZoom({ scale: 1, tx: 0, ty: 0 });

  // Ctrl/Cmd + wheel to zoom around the pointer.
  const onWheel = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    const wrap = wrapRef.current;
    const rect = wrap.getBoundingClientRect();
    const focalX = e.clientX - rect.left;
    const focalY = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    zoomAt(zoom.scale * factor, focalX, focalY);
  };

  // Distance between two touch points.
  const touchDist = (t0, t1) => Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
  const touchMid = (t0, t1) => [(t0.clientX + t1.clientX) / 2, (t0.clientY + t1.clientY) / 2];

  const isTwoPoint = (t) => ["line", "curve", "rect", "boxcross", "ellipse", "arrow"].includes(t);
  const isPoint = (t) => ["text", "depth", "symbol"].includes(t);

  const start = (e) => {
    e.preventDefault();
    const [nx, ny] = posFromEvent(e);
    const u = getUtil(activeService);
    const base = {
      id: crypto.randomUUID(),
      service: activeService,
      color: u.color,
      width: brushSize,
      type: tool,
    };
    if (tool === "pen") {
      setCurrent({ ...base, points: [[nx, ny]] });
    } else if (isTwoPoint(tool)) {
      setCurrent({ ...base, points: [[nx, ny], [nx, ny]] });
    } else if (isPoint(tool)) {
      // Handled in end() via prompt so the click position is the anchor.
      setCurrent({ ...base, points: [[nx, ny]], text: "" });
    }
  };

  const move = (e) => {
    if (!current) return;
    e.preventDefault();
    const [nx, ny] = posFromEvent(e);
    if (current.type === "pen") {
      setCurrent({ ...current, points: [...current.points, [nx, ny]] });
    } else if (isTwoPoint(current.type)) {
      setCurrent({ ...current, points: [current.points[0], [nx, ny]] });
    }
  };

  const end = () => {
    if (!current) return;
    const commit = (shape) => onChange({ strokes: [...(job.strokes || []), shape] });

    if (current.type === "pen") {
      if (current.points.length >= 2) commit(current);
    } else if (isTwoPoint(current.type)) {
      const [p1, p2] = current.points;
      const dx = Math.abs(p1[0] - p2[0]) * size.w;
      const dy = Math.abs(p1[1] - p2[1]) * size.h;
      if (dx + dy < 4) { setCurrent(null); return; } // ignore accidental taps
      if (current.type === "boxcross") {
        // Prompt for a pit type — Meter / Valve / IO / Manhole / Grease trap
        // are the common AU tradie labels; user can also type a custom value
        // or leave blank for an unlabeled symbol.
        const val = window.prompt(
          "Pit type (Meter · Valve · IO · Manhole · Grease trap)",
          "Meter"
        );
        commit({ ...current, text: (val || "").trim() });
      } else {
        commit(current);
      }
    } else if (isPoint(current.type)) {
      let promptMsg = "Label text";
      let suggestion = "";
      if (current.type === "depth") {
        promptMsg = "Depth in mm (e.g. 400)";
        suggestion = "400";
      } else if (current.type === "symbol") {
        promptMsg = `AS site symbol code (${AS_HINT_LIST})`;
        suggestion = "IO";
      }
      const val = window.prompt(promptMsg, suggestion);
      if (val && val.trim()) {
        let text = val.trim();
        if (current.type === "depth") text = `@ ${text}mm`;
        if (current.type === "symbol") text = text.toUpperCase();
        commit({ ...current, text });
        // Toast the full label so tradies see what the code stands for.
        if (current.type === "symbol") {
          const full = AS_LABEL_BY_CODE[text];
          if (full) window.setTimeout(() => {
            // Non-blocking hint via title bar — sonner toast lives in the
            // parent tree; using console keeps this component prop-free.
            // eslint-disable-next-line no-console
            console.info(`AS symbol ${text}: ${full}`);
          }, 0);
        }
      }
    }
    setCurrent(null);
  };

  const undo = () => {
    const s = job.strokes || [];
    if (s.length === 0) return;
    onChange({ strokes: s.slice(0, -1) });
  };
  const clearAll = () => {
    if (!(job.strokes || []).length) return;
    if (window.confirm("Clear all drawings?")) onChange({ strokes: [] });
  };

  return (
    <div id="fieldplan-sketch-capture" className="relative w-full h-full bg-white" ref={wrapRef}>
      {backgroundUrl ? (
        <img src={backgroundUrl} alt="site" className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none" />
      ) : (
        <div className="absolute inset-0 bp-hatch pointer-events-none" />
      )}
      <canvas
        ref={canvasRef}
        data-testid="sketch-canvas"
        className="absolute inset-0 w-full h-full no-touch-action cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
        <button data-testid="sketch-undo" onClick={undo} className="btn-ghost-rugged text-[11px] py-1.5 px-2">Undo</button>
        <button data-testid="sketch-clear" onClick={clearAll} className="btn-ghost-rugged text-[11px] py-1.5 px-2">Clear</button>
      </div>
    </div>
  );
};

export default SketchCanvas;
